import { useTranslation } from 'react-i18next';
import {
    ChipSelect,
    ChipSelectOption,
} from 'src/components/ChipSelect/ChipSelect';
import { AnalyticsFilter } from 'src/domain/analytics';
import { Organization } from 'src/domain/organization';

export type DepartmentSelection = Pick<
    AnalyticsFilter,
    'departmentId' | 'departmentOrganizationId'
>;

const NO_DEPARTMENT = 'none';

const optionId = ({
    departmentId,
    departmentOrganizationId,
}: DepartmentSelection) =>
    departmentId === NO_DEPARTMENT
        ? NO_DEPARTMENT
        : JSON.stringify([departmentOrganizationId, departmentId]);

const selectionFromId = (id: string): DepartmentSelection => {
    if (id === NO_DEPARTMENT) return { departmentId: NO_DEPARTMENT };
    const [departmentOrganizationId, departmentId] = JSON.parse(id) as [
        string,
        string,
    ];
    return { departmentId, departmentOrganizationId };
};

/** Single-choice department filter styled like the organization filter. */
export const DepartmentFilter = ({
    organizations,
    selection,
    onChange,
    disabled,
}: {
    organizations: Organization[];
    selection: DepartmentSelection;
    onChange: (selection: DepartmentSelection) => void;
    disabled?: boolean;
}) => {
    const { t } = useTranslation();
    const showOrganization = organizations.length > 1;
    const options: ChipSelectOption[] = [
        { id: NO_DEPARTMENT, label: t('creators.form.noDepartment') },
        ...organizations.flatMap(org => {
            const organizationName =
                org.organizationDisplayName || org.organizationName || org.id;
            return (org.groups ?? [])
                .filter(group => !group.parentId)
                .map(group => ({
                    id: optionId({
                        departmentId: group.id,
                        departmentOrganizationId: org.id,
                    }),
                    label: showOrganization
                        ? `${group.name} · ${organizationName}`
                        : group.name,
                }));
        }),
    ];
    const selectedId = selection.departmentId ? optionId(selection) : undefined;

    return (
        <ChipSelect
            className="analytics-department-filter"
            ariaLabel={t('admin.analytics.department')}
            options={options}
            selectedIds={selectedId ? [selectedId] : []}
            onChange={ids => {
                const next = ids.find(id => id !== selectedId);
                onChange(next ? selectionFromId(next) : {});
            }}
            placeholder={t('admin.analytics.allDepartments')}
            disabled={disabled}
            closeOnSelect
        />
    );
};
